import React, { Component, createRef } from 'react';
import { fabric } from 'fabric';
import { GOOGLE_FONTS, loadGoogleFont } from './GoogleFonts';
import './DesignEditor.css';

// Largest canvas dimension used when sizing the canvas to a product image.
// Keeps the editor responsive and bounds the exported base64 payload (it is
// POSTed inside the product form, under the API's 30 MB body limit) while
// preserving the resolution of any normal product photo (originals are
// typically well under this).
const MAX_EDIT_DIM = 2560;

// Pasteboard margin (artboard units) around the artboard. The fabric canvas is
// BLEED px larger on every side with the viewport translated by BLEED, so an
// object's resize/rotate controls stay visible when it's dragged outside the
// artboard. The artboard (the exported region) is the inset frame; export crops
// back to it.
const BLEED = 160;

// ---------------------------------------------------------------------------
// Canvas presets (w × h) for common event-image formats
// ---------------------------------------------------------------------------
const PRESETS = [
  { label: 'Logo (400×400)', w: 400, h: 400 },
  { label: 'Calendar Card (400×400)', w: 400, h: 400 },
  { label: 'Wide Banner (1200×630)', w: 1200, h: 630 },
  { label: 'Social Preview (1200×630)', w: 1200, h: 630 },
  { label: 'Story (1080×1920)', w: 1080, h: 1920 },
  { label: 'Custom', w: null, h: null },
];

class DesignEditor extends Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.canvasAreaRef = React.createRef();
    this.canvasSizerRef = React.createRef();
    this.editorRef = React.createRef();
    this.fileInputRef = React.createRef();
    this.fontPickerRef = createRef();

    const preset = props.preset || PRESETS[0];
    this.state = {
      canvasWidth: preset.w || 400,
      canvasHeight: preset.h || 400,
      activeTool: 'select',
      activeObject: null,
      fillColor: '#ffffff',
      strokeColor: '#000000',
      fontSize: 32,
      fontFamily: 'Arial',
      fontBold: false,
      fontItalic: false,
      fontSearch: '',
      fontDropdownOpen: false,
      opacity: 1,
      history: [],
      historyIndex: -1,
      zoom: 1,
      showGrid: false,
      showEventImages: false,
      bgColor: '',
      removingBg: false,
      upscaling: false,
      showUpscaleMenu: false,
      magicEraseMode: false,
      magicEraseApplying: false,
      magicEraseBrushSize: 30,
      shadowEnabled: false,
      shadowColor: '#000000',
      shadowBlur: 10,
      shadowOffsetX: 4,
      shadowOffsetY: 4,
      layers: [],
      errorMessage: null,
    };
  }

  // Transient, in-editor error banner. The editor's modal sits above the
  // evergreen toaster z-index, so errors are shown inside the editor itself.
  _showError = (msg) => {
    this.setState({ errorMessage: msg });
    clearTimeout(this._errTimer);
    this._errTimer = setTimeout(() => this.setState({ errorMessage: null }), 6000);
  };

  // ===========================================================================
  // Lifecycle
  // ===========================================================================
  componentDidMount() {
    this.initCanvas();
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('paste', this.handlePaste);
    this._setupMiddleMousePan();
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('paste', this.handlePaste);
    this._teardownMiddleMousePan();
    clearTimeout(this._errTimer);
    if (this._editorEl) {
      this._editorEl.removeEventListener('dragover', this.handleDragOver);
      this._editorEl.removeEventListener('drop', this.handleDrop);
    }
    if (this.canvas) {
      this.canvas.dispose();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Report unsaved-changes state to the modal: anything past the initial
    // baseline (index 0) is a real user edit.
    if (prevState.historyIndex !== this.state.historyIndex && this.props.onDirtyChange) {
      this.props.onDirtyChange(this.state.historyIndex > 0);
    }
  }

  initCanvas = () => {
    const { canvasWidth, canvasHeight } = this.state;
    this.canvas = new fabric.Canvas(this.canvasRef.current, {
      width: canvasWidth + 2 * BLEED,
      height: canvasHeight + 2 * BLEED,
      backgroundColor: null,
      preserveObjectStacking: true,
      selection: true,
    });
    // Inset the artboard by BLEED so the surrounding pasteboard shows controls.
    this.canvas.setViewportTransform([1, 0, 0, 1, BLEED, BLEED]);

    this.canvas.on('selection:created', this.handleSelection);
    this.canvas.on('selection:updated', this.handleSelection);
    this.canvas.on('selection:cleared', () => this.setState({ activeObject: null }));
    this.canvas.on('object:modified', () => { this.saveHistory(); this.updateLayers(); });
    this.canvas.on('object:added', () => { this.updateLayers(); });
    this.canvas.on('object:removed', () => { this.updateLayers(); });

    // Fit canvas in viewport
    this.fitCanvasInView();

    // Set up drag & drop on this editor instance's root (ref, not a global
    // querySelector, so multiple editors never cross-wire).
    this._editorEl = this.editorRef.current;
    if (this._editorEl) {
      this._editorEl.addEventListener('dragover', this.handleDragOver);
      this._editorEl.addEventListener('drop', this.handleDrop);
    }

    // Load saved design JSON if available (restores all layers)
    if (this.props.designJson) {
      try {
        this.canvas.loadFromJSON(this._prepareJsonForLoad(this.props.designJson), () => {
          this.canvas.renderAll();
          this.fitCanvasInView();
          this.saveHistory();
          this.updateLayers();
        });
        return; // skip flat image loading — JSON has everything
      } catch (e) {
        console.warn('Failed to load design JSON, falling back to flat image:', e);
      }
    }

    // Load the initial image. The async loaders write the single baseline
    // history entry themselves (in their image-loaded callback), so the first
    // Undo can never wipe the freshly-loaded image. Only the empty-canvas case
    // saves its baseline here.
    if (this.props.initialImage) {
      if (this.props.sizeToImage) {
        this.loadProductImage(this.props.initialImage);
      } else {
        this.loadImageOnCanvas(this.props.initialImage, true).then((ok) => {
          this.fitCanvasInView();
          if (!ok) this.saveHistory(); // baseline even if the image failed to load
        });
      }
    } else {
      this.saveHistory();
    }
  };

  fitCanvasInView = () => {
    const container = this.canvasAreaRef.current;
    if (!container) return;
    const { canvasWidth, canvasHeight } = this.state;
    const maxW = container.clientWidth - 80; // leave 40px padding each side
    const maxH = container.clientHeight - 80;
    // Fit the whole pasteboard (artboard + bleed) so the gutter is reachable.
    const zoom = Math.min(maxW / (canvasWidth + 2 * BLEED), maxH / (canvasHeight + 2 * BLEED), 1);
    this.setState({ zoom }, () => this.scrollCanvasToCenter());
  };

  scrollCanvasToCenter = () => {
    const container = this.canvasAreaRef.current;
    if (!container) return;
    // Let the DOM update first
    requestAnimationFrame(() => {
      const scrollW = container.scrollWidth - container.clientWidth;
      const scrollH = container.scrollHeight - container.clientHeight;
      container.scrollLeft = scrollW / 2;
      container.scrollTop = scrollH / 2;
    });
  };

  // ===========================================================================
  // Zoom controls
  // ===========================================================================
  _clampZoom = (z) => Math.min(Math.max(z, 0.1), 5);

  /**
   * Zoom to `nextZoom`, keeping the point at (clientX, clientY) under the
   * cursor. Measures the canvas "sizer" element before and after the zoom and
   * compensates with scroll, so it works regardless of the centering padding.
   * When the canvas is smaller than the viewport there is no scroll room and
   * this gracefully degrades to centered zoom.
   */
  _zoomAtPoint = (nextZoom, clientX, clientY) => {
    const zoom = this._clampZoom(nextZoom);
    const container = this.canvasAreaRef.current;
    const sizer = this.canvasSizerRef.current;
    if (!container || !sizer) { this.setState({ zoom }); return; }

    const before = sizer.getBoundingClientRect();
    const fx = before.width ? (clientX - before.left) / before.width : 0.5;
    const fy = before.height ? (clientY - before.top) / before.height : 0.5;

    this.setState({ zoom }, () => {
      const after = sizer.getBoundingClientRect();
      container.scrollLeft += (after.left + fx * after.width) - clientX;
      container.scrollTop += (after.top + fy * after.height) - clientY;
    });
  };

  _zoomFromCenter = (nextZoom) => {
    const container = this.canvasAreaRef.current;
    if (!container) { this.setState({ zoom: this._clampZoom(nextZoom) }); return; }
    const r = container.getBoundingClientRect();
    this._zoomAtPoint(nextZoom, r.left + r.width / 2, r.top + r.height / 2);
  };

  zoomIn = () => this._zoomFromCenter(this.state.zoom * 1.25);
  zoomOut = () => this._zoomFromCenter(this.state.zoom * 0.8);
  zoomTo100 = () => this.setState({ zoom: 1 }, () => this.scrollCanvasToCenter());

  // ===========================================================================
  // Middle-mouse pan
  // ===========================================================================
  _setupMiddleMousePan = () => {
    this._panState = { active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 };
    this._onPanMouseDown = (e) => {
      if (e.button !== 1) return; // middle button only
      e.preventDefault();
      const container = this.canvasAreaRef.current;
      if (!container) return;
      this._panState = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
      };
      container.style.cursor = 'grabbing';
    };
    this._onPanMouseMove = (e) => {
      if (!this._panState.active) return;
      e.preventDefault();
      const container = this.canvasAreaRef.current;
      if (!container) return;
      const dx = e.clientX - this._panState.startX;
      const dy = e.clientY - this._panState.startY;
      container.scrollLeft = this._panState.scrollLeft - dx;
      container.scrollTop = this._panState.scrollTop - dy;
    };
    this._onPanMouseUp = (e) => {
      if (!this._panState.active) return;
      this._panState.active = false;
      const container = this.canvasAreaRef.current;
      if (container) container.style.cursor = '';
    };
    // Prevent middle-click auto-scroll icon
    this._onAuxClick = (e) => { if (e.button === 1) e.preventDefault(); };

    // Scroll-wheel zoom — multiplicative (uniform feel across the range) and
    // anchored to the pointer so the spot under the cursor stays put. Small
    // per-notch step (~4%) so a single tick nudges the zoom gently instead of
    // jumping in big increments.
    this._onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.96 : 1.04;
      this._zoomAtPoint(this.state.zoom * factor, e.clientX, e.clientY);
    };

    window.addEventListener('mousemove', this._onPanMouseMove);
    window.addEventListener('mouseup', this._onPanMouseUp);
    // Attach mousedown to the canvas area specifically
    const area = this.canvasAreaRef.current;
    if (area) {
      area.addEventListener('mousedown', this._onPanMouseDown);
      area.addEventListener('auxclick', this._onAuxClick);
      area.addEventListener('wheel', this._onWheel, { passive: false });
    }
  };

  _teardownMiddleMousePan = () => {
    window.removeEventListener('mousemove', this._onPanMouseMove);
    window.removeEventListener('mouseup', this._onPanMouseUp);
    const area = this.canvasAreaRef.current;
    if (area) {
      area.removeEventListener('mousedown', this._onPanMouseDown);
      area.removeEventListener('auxclick', this._onAuxClick);
      area.removeEventListener('wheel', this._onWheel);
    }
  };

  // ===========================================================================
  // History (Undo / Redo)
  // ===========================================================================
  saveHistory = () => {
    const json = JSON.stringify(this.canvas.toJSON(['name', 'crossOrigin']));
    this.setState(prev => {
      const history = prev.history.slice(0, prev.historyIndex + 1);
      history.push(json);
      return { history, historyIndex: history.length - 1 };
    });
  };

  undo = () => {
    this.setState(prev => {
      if (prev.historyIndex <= 0) return null;
      const newIndex = prev.historyIndex - 1;
      this.loadFromHistory(prev.history[newIndex]);
      return { historyIndex: newIndex };
    });
  };

  redo = () => {
    this.setState(prev => {
      if (prev.historyIndex >= prev.history.length - 1) return null;
      const newIndex = prev.historyIndex + 1;
      this.loadFromHistory(prev.history[newIndex]);
      return { historyIndex: newIndex };
    });
  };

  /**
   * Inject crossOrigin: 'anonymous' into every image object in a fabric JSON
   * string so that images loaded from URLs remain un-tainted and can be
   * exported to base64 later.
   */
  _prepareJsonForLoad = (jsonStr) => {
    try {
      const parsed = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
      if (parsed.objects) {
        parsed.objects.forEach(obj => {
          if (obj.type === 'image') {
            obj.crossOrigin = 'anonymous';
          }
        });
      }
      return parsed;
    } catch (e) {
      return jsonStr;
    }
  };

  loadFromHistory = (json) => {
    this.canvas.loadFromJSON(this._prepareJsonForLoad(json), () => {
      this.canvas.renderAll();
      this.updateLayers();
    });
  };

  // ===========================================================================
  // Keyboard shortcuts
  // ===========================================================================
  handleKeyDown = (e) => {
    // Don't intercept when typing in an input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    const ctrl = e.ctrlKey || e.metaKey;

    if (ctrl && e.key === 'z') {
      e.preventDefault();
      if (e.shiftKey) this.redo(); else this.undo();
    }
    if (ctrl && e.key === 'y') { e.preventDefault(); this.redo(); }
    if (ctrl && (e.key === '+' || e.key === '=')) { e.preventDefault(); this.zoomIn(); }
    if (ctrl && (e.key === '-' || e.key === '_')) { e.preventDefault(); this.zoomOut(); }
    if (ctrl && e.key === '0') { e.preventDefault(); this.zoomTo100(); }
    if (ctrl && e.key === 'c') { e.preventDefault(); this.copyObject(); }
    if (ctrl && e.key === 'v') {
      // Don't preventDefault — let the native 'paste' event fire so
      // handlePaste can check for clipboard images first.  If no image
      // is found in the clipboard, handlePaste will call pasteObject()
      // as a fallback (internal fabric.js copy/paste).
      this._pasteTriggeredByKeyboard = true;
      return;
    }
    if (ctrl && e.key === 'd') { e.preventDefault(); this.duplicateObject(); }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.state.activeObject) { e.preventDefault(); this.deleteSelected(); }
    }
  };

  // ===========================================================================
  // Selection & Properties panel
  // ===========================================================================
  handleSelection = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj) { this.setState({ activeObject: null }); return; }
    const shadow = obj.shadow;
    this.setState({
      activeObject: obj,
      fillColor: obj.fill || '#ffffff',
      strokeColor: obj.stroke || '#000000',
      opacity: obj.opacity ?? 1,
      fontSize: obj.fontSize || 32,
      fontFamily: obj.fontFamily || 'Arial',
      fontBold: obj.fontWeight === 'bold',
      fontItalic: obj.fontStyle === 'italic',
      shadowEnabled: !!shadow,
      shadowColor: shadow?.color || '#000000',
      shadowBlur: shadow?.blur ?? 10,
      shadowOffsetX: shadow?.offsetX ?? 4,
      shadowOffsetY: shadow?.offsetY ?? 4,
    });
  };

  // ===========================================================================
  // Tools
  // ===========================================================================
  setTool = (tool) => {
    this.setState({ activeTool: tool });
    this.canvas.isDrawingMode = tool === 'draw';
    if (tool === 'draw') {
      this.canvas.freeDrawingBrush.color = this.state.strokeColor;
      this.canvas.freeDrawingBrush.width = 3;
    }
    if (tool === 'select') {
      this.canvas.selection = true;
    }
  };

  addText = () => {
    const text = new fabric.IText('Text', {
      left: this.state.canvasWidth / 2 - 40,
      top: this.state.canvasHeight / 2 - 20,
      fontFamily: this.state.fontFamily,
      fontSize: this.state.fontSize,
      fill: this.state.fillColor,
      name: 'Text',
    });
    this.canvas.add(text);
    this.canvas.setActiveObject(text);
    this.canvas.renderAll();
    this.saveHistory();
    this.setTool('select');
  };

  addRect = () => {
    const rect = new fabric.Rect({
      left: this.state.canvasWidth / 2 - 50,
      top: this.state.canvasHeight / 2 - 50,
      width: 100,
      height: 100,
      fill: this.state.fillColor,
      stroke: this.state.strokeColor,
      strokeWidth: 2,
      rx: 0,
      ry: 0,
      name: 'Rectangle',
    });
    this.canvas.add(rect);
    this.canvas.setActiveObject(rect);
    this.canvas.renderAll();
    this.saveHistory();
    this.setTool('select');
  };

  addCircle = () => {
    const circle = new fabric.Circle({
      left: this.state.canvasWidth / 2 - 40,
      top: this.state.canvasHeight / 2 - 40,
      radius: 40,
      fill: this.state.fillColor,
      stroke: this.state.strokeColor,
      strokeWidth: 2,
      name: 'Circle',
    });
    this.canvas.add(circle);
    this.canvas.setActiveObject(circle);
    this.canvas.renderAll();
    this.saveHistory();
    this.setTool('select');
  };

  addLine = () => {
    const line = new fabric.Line(
      [50, this.state.canvasHeight / 2, this.state.canvasWidth - 50, this.state.canvasHeight / 2],
      {
        stroke: this.state.strokeColor,
        strokeWidth: 3,
        name: 'Line',
      }
    );
    this.canvas.add(line);
    this.canvas.setActiveObject(line);
    this.canvas.renderAll();
    this.saveHistory();
    this.setTool('select');
  };

  // ===========================================================================
  // Image handling
  // ===========================================================================
  triggerImageUpload = () => {
    this.fileInputRef.current.click();
  };

  handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      this.loadImageOnCanvas(ev.target.result, false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ===========================================================================
  // Drag & Drop (files from OS or images dragged from the web)
  // ===========================================================================
  handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // 1. Check for dropped files (from OS file explorer)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      Array.from(e.dataTransfer.files).forEach(file => {
        if (!file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (ev) => this.loadImageOnCanvas(ev.target.result, false);
        reader.readAsDataURL(file);
      });
      return;
    }

    // 2. Check for image URL dragged from the browser / another tab
    const html = e.dataTransfer.getData('text/html');
    if (html) {
      const match = html.match(/<img[^>]+src="([^"]+)"/i);
      if (match && match[1]) {
        this.loadImageOnCanvas(match[1], false);
        return;
      }
    }

    // 3. Plain text URL (e.g. dragged link)
    //    Accept common image extensions + extensionless CDN URLs (try to load
    //    anything that looks like an HTTP URL — loadImageOnCanvas will silently
    //    discard anything fabric can't decode).
    const url = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text/uri-list');
    if (url && /^https?:\/\/.+/i.test(url)) {
      this.loadImageOnCanvas(url, false);
    }
  };

  /**
   * Convert a URL to a base64 data URL via fetch, avoiding canvas CORS taint.
   * Returns the original src if it's already a data: URL or if fetching fails.
   */
  _toBase64 = async (src) => {
    if (!src || src.startsWith('data:')) return src;
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(src);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      // CORS fetch failed — fall back to drawing via an <img> element
      console.warn('Fetch failed, falling back to img→canvas:', e);
      try {
        return await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            try { resolve(c.toDataURL('image/png')); }
            catch (_) { resolve(src); }
          };
          img.onerror = () => resolve(src);
          img.src = src;
        });
      } catch (_) {
        return src;
      }
    }
  };

  // Size the fabric element to the artboard plus the pasteboard bleed and
  // re-inset the viewport. `w`/`h` are artboard (export) dimensions.
  _applyArtboard = (w, h) => {
    this.canvas.setWidth(w + 2 * BLEED);
    this.canvas.setHeight(h + 2 * BLEED);
    this.canvas.setViewportTransform([1, 0, 0, 1, BLEED, BLEED]);
  };

  // Resize the fabric canvas without the user-facing side effects of
  // resizeCanvas (no history entry / no auto-fit) — used while loading a
  // product image so a single baseline is saved afterwards.
  _setCanvasSize = (w, h, cb) => {
    this._applyArtboard(w, h);
    this.setState({ canvasWidth: w, canvasHeight: h }, cb);
  };

  /**
   * Product-image edit entry point: size the canvas to the image (capped at
   * MAX_EDIT_DIM, aspect preserved) and place it 1:1, so nothing is cropped and
   * the export keeps the original resolution/aspect. Writes the single baseline
   * history entry once the image and canvas size are settled.
   */
  loadProductImage = async (src) => {
    const safeSrc = await this._toBase64(src);
    return new Promise((resolve) => {
      fabric.Image.fromURL(safeSrc, (img) => {
        if (!img || !img.width) {
          console.warn('Failed to load product image on canvas:', src);
          this._showError('Imaginea nu a putut fi încărcată în editor.');
          this.saveHistory(); // keep a (blank) baseline so the editor is usable
          resolve(false);
          return;
        }

        const natW = img.width;
        const natH = img.height;
        const downscale = Math.min(1, MAX_EDIT_DIM / Math.max(natW, natH));
        const cw = Math.max(1, Math.round(natW * downscale));
        const ch = Math.max(1, Math.round(natH * downscale));

        img.set({
          originX: 'left',
          originY: 'top',
          left: 0,
          top: 0,
          scaleX: cw / natW,
          scaleY: ch / natH,
          name: 'Imagine produs',
          selectable: true,
        });

        this._setCanvasSize(cw, ch, () => {
          this.canvas.add(img);
          this.canvas.sendToBack(img);
          this.canvas.setActiveObject(img);
          this.canvas.renderAll();
          this.fitCanvasInView();
          this.saveHistory();
          this.updateLayers();
          resolve(true);
        });
      }, { crossOrigin: 'anonymous' });
    });
  };

  loadImageOnCanvas = async (src, asBackground = false) => {
    // Returns a promise that resolves once the image is on the canvas (or has
    // failed), so callers can sequence a single history baseline after it.
    // Convert external URLs to base64 to avoid CORS / canvas taint issues.
    const safeSrc = await this._toBase64(src);
    return new Promise((resolve) => {
      fabric.Image.fromURL(safeSrc, (img) => {
        if (!img || !img.width) {
          console.warn('Failed to load image on canvas:', src);
          this._showError('Imaginea nu a putut fi încărcată în editor.');
          resolve(false);
          return;
        }
        const { canvasWidth, canvasHeight } = this.state;

        if (asBackground) {
          // Scale to fill the canvas
          const scaleX = canvasWidth / img.width;
          const scaleY = canvasHeight / img.height;
          const scale = Math.max(scaleX, scaleY);
          img.set({
            scaleX: scale,
            scaleY: scale,
            left: canvasWidth / 2,
            top: canvasHeight / 2,
            originX: 'center',
            originY: 'center',
            selectable: true,
            name: 'Background',
          });
          // Send to back
          this.canvas.add(img);
          this.canvas.sendToBack(img);
        } else {
          // Scale to fit nicely (max 60% of canvas)
          const maxW = canvasWidth * 0.6;
          const maxH = canvasHeight * 0.6;
          const scale = Math.min(maxW / img.width, maxH / img.height, 1);
          img.set({
            scaleX: scale,
            scaleY: scale,
            left: canvasWidth / 2,
            top: canvasHeight / 2,
            originX: 'center',
            originY: 'center',
            name: 'Image',
          });
          this.canvas.add(img);
        }
        this.canvas.setActiveObject(img);
        this.canvas.renderAll();
        this.saveHistory();
        resolve(true);
      }, { crossOrigin: 'anonymous' });
    });
  };

  // ===========================================================================
  // Object manipulation
  // ===========================================================================
  deleteSelected = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    if (obj.type === 'activeSelection') {
      obj.forEachObject(o => this.canvas.remove(o));
      this.canvas.discardActiveObject();
    } else {
      this.canvas.remove(obj);
    }
    this.canvas.renderAll();
    this.saveHistory();
  };

  // ---------------------------------------------------------------------------
  // Clipboard paste – images from external apps (Canva, Photoshop, browser…)
  // ---------------------------------------------------------------------------
  handlePaste = (e) => {
    // Don't intercept when typing in an input / textarea
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      this._pasteTriggeredByKeyboard = false;
      return;
    }

    const items = e.clipboardData && e.clipboardData.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (!blob) continue;
          const reader = new FileReader();
          reader.onload = (ev) => {
            this.loadImageOnCanvas(ev.target.result, false);
          };
          reader.readAsDataURL(blob);
          this._pasteTriggeredByKeyboard = false;
          return; // handled — stop looking
        }
      }
    }

    // No image in system clipboard — fall back to internal fabric paste
    if (this._pasteTriggeredByKeyboard) {
      e.preventDefault();
      this.pasteObject();
    }
    this._pasteTriggeredByKeyboard = false;
  };

  copyObject = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    obj.clone(cloned => { this._clipboard = cloned; });
  };

  pasteObject = () => {
    if (!this._clipboard) return;
    this._clipboard.clone(cloned => {
      this.canvas.discardActiveObject();
      cloned.set({ left: cloned.left + 20, top: cloned.top + 20, evented: true });
      if (cloned.type === 'activeSelection') {
        cloned.canvas = this.canvas;
        cloned.forEachObject(o => this.canvas.add(o));
        cloned.setCoords();
      } else {
        this.canvas.add(cloned);
      }
      this._clipboard.top += 20;
      this._clipboard.left += 20;
      this.canvas.setActiveObject(cloned);
      this.canvas.renderAll();
      this.saveHistory();
    });
  };

  duplicateObject = () => {
    this.copyObject();
    setTimeout(() => this.pasteObject(), 50);
  };

  bringForward = () => {
    const obj = this.canvas.getActiveObject();
    if (obj) { this.canvas.bringForward(obj); this.canvas.renderAll(); this.saveHistory(); this.updateLayers(); }
  };

  sendBackward = () => {
    const obj = this.canvas.getActiveObject();
    if (obj) { this.canvas.sendBackwards(obj); this.canvas.renderAll(); this.saveHistory(); this.updateLayers(); }
  };

  bringToFront = () => {
    const obj = this.canvas.getActiveObject();
    if (obj) { this.canvas.bringToFront(obj); this.canvas.renderAll(); this.saveHistory(); this.updateLayers(); }
  };

  sendToBack = () => {
    const obj = this.canvas.getActiveObject();
    if (obj) { this.canvas.sendToBack(obj); this.canvas.renderAll(); this.saveHistory(); this.updateLayers(); }
  };

  // ===========================================================================
  // Property changes
  // ===========================================================================
  handleFillChange = (e) => {
    const color = e.target.value;
    this.setState({ fillColor: color });
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.set('fill', color); this.canvas.renderAll(); }
  };

  handleStrokeChange = (e) => {
    const color = e.target.value;
    this.setState({ strokeColor: color });
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.set('stroke', color); this.canvas.renderAll(); }
  };

  handleOpacityChange = (e) => {
    const val = parseFloat(e.target.value);
    this.setState({ opacity: val });
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.set('opacity', val); this.canvas.renderAll(); }
  };

  handleFontSizeChange = (e) => {
    const val = parseInt(e.target.value, 10);
    this.setState({ fontSize: val });
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === 'i-text') { obj.set('fontSize', val); this.canvas.renderAll(); }
  };

  handleFontFamilyChange = async (fontFamily) => {
    this.setState({ fontFamily, fontSearch: '', fontDropdownOpen: false });
    await loadGoogleFont(fontFamily);
    const obj = this.canvas.getActiveObject();
    if (obj && obj.type === 'i-text') {
      obj.set('fontFamily', fontFamily);
      this.canvas.renderAll();
    }
  };

  insertEventImage = (src) => {
    this.loadImageOnCanvas(src, false);
    this.setState({ showEventImages: false });
  };

  getFilteredFonts = () => {
    const q = this.state.fontSearch.toLowerCase();
    if (!q) return GOOGLE_FONTS.slice(0, 50); // show top 50 when no search
    return GOOGLE_FONTS.filter(f => f.toLowerCase().includes(q)).slice(0, 30);
  };

  toggleBold = () => {
    this.setState(prev => {
      const bold = !prev.fontBold;
      const obj = this.canvas.getActiveObject();
      if (obj && obj.type === 'i-text') { obj.set('fontWeight', bold ? 'bold' : 'normal'); this.canvas.renderAll(); }
      return { fontBold: bold };
    });
  };

  toggleItalic = () => {
    this.setState(prev => {
      const italic = !prev.fontItalic;
      const obj = this.canvas.getActiveObject();
      if (obj && obj.type === 'i-text') { obj.set('fontStyle', italic ? 'italic' : 'normal'); this.canvas.renderAll(); }
      return { fontItalic: italic };
    });
  };

  // ===========================================================================
  // Shadow / Glow
  // ===========================================================================
  _applyShadow = (updates = {}) => {
    const state = { ...this.state, ...updates };
    const obj = this.canvas.getActiveObject();
    if (!obj) return;

    if (!state.shadowEnabled) {
      obj.set('shadow', null);
    } else {
      obj.set('shadow', new fabric.Shadow({
        color: state.shadowColor,
        blur: state.shadowBlur,
        offsetX: state.shadowOffsetX,
        offsetY: state.shadowOffsetY,
      }));
    }
    this.canvas.renderAll();
  };

  toggleShadow = () => {
    this.setState(prev => {
      const enabled = !prev.shadowEnabled;
      const updates = { shadowEnabled: enabled };
      this._applyShadow(updates);
      return updates;
    });
  };

  handleShadowColor = (e) => {
    const color = e.target.value;
    this.setState({ shadowColor: color }, () => this._applyShadow());
  };

  handleShadowBlur = (e) => {
    const blur = parseInt(e.target.value, 10);
    this.setState({ shadowBlur: blur }, () => this._applyShadow());
  };

  handleShadowOffsetX = (e) => {
    const val = parseInt(e.target.value, 10);
    this.setState({ shadowOffsetX: val }, () => this._applyShadow());
  };

  handleShadowOffsetY = (e) => {
    const val = parseInt(e.target.value, 10);
    this.setState({ shadowOffsetY: val }, () => this._applyShadow());
  };

  applyGlowPreset = () => {
    this.setState({
      shadowEnabled: true,
      shadowColor: '#00aaff',
      shadowBlur: 20,
      shadowOffsetX: 0,
      shadowOffsetY: 0,
    }, () => this._applyShadow());
  };

  handleBgColorChange = (e) => {
    const color = e.target.value;
    this.canvas.backgroundColor = color;
    this.canvas.renderAll();
    this.setState({ bgColor: color });
  };

  clearBgColor = () => {
    this.canvas.backgroundColor = null;
    this.canvas.renderAll();
    this.setState({ bgColor: '' });
  };

  // ===========================================================================
  // Canvas size
  // ===========================================================================
  handlePresetChange = (e) => {
    const idx = parseInt(e.target.value, 10);
    const preset = PRESETS[idx];
    if (preset.w && preset.h) {
      this.resizeCanvas(preset.w, preset.h);
    }
  };

  resizeCanvas = (w, h) => {
    this.setState({ canvasWidth: w, canvasHeight: h }, () => {
      this._applyArtboard(w, h);
      this.canvas.renderAll();
      this.fitCanvasInView();
      this.saveHistory();
    });
  };

  // ===========================================================================
  // Layers panel
  // ===========================================================================
  updateLayers = () => {
    const objects = this.canvas.getObjects();
    const layers = objects.map((obj, i) => ({
      index: i,
      name: obj.name || obj.type || `Layer ${i}`,
      visible: obj.visible !== false,
      type: obj.type,
    })).reverse(); // top-most first
    this.setState({ layers });
  };

  toggleLayerVisibility = (idx) => {
    const obj = this.canvas.item(idx);
    if (obj) {
      obj.visible = !obj.visible;
      this.canvas.renderAll();
      this.updateLayers();
    }
  };

  selectLayer = (idx) => {
    const obj = this.canvas.item(idx);
    if (obj) {
      this.canvas.setActiveObject(obj);
      this.canvas.renderAll();
    }
  };

  // ===========================================================================
  // Export
  // ===========================================================================
  exportAsImage = (format = 'png') => {
    const { canvasWidth, canvasHeight } = this.state;
    // Drop the pasteboard offset so object-space (0,0)–(W,H) maps to the crop
    // origin, then capture only the artboard.
    const vt = this.canvas.viewportTransform;
    this.canvas.discardActiveObject();
    this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    this.canvas.renderAll();

    const dataURL = this.canvas.toDataURL({
      format,
      quality: format === 'jpeg' ? 0.9 : 1,
      width: canvasWidth,
      height: canvasHeight,
      multiplier: 1,
    });

    this.canvas.setViewportTransform(vt);
    this.canvas.renderAll();
    return dataURL;
  };

  exportDesignJson = () => {
    // Serialize the full canvas state, embedding all images as base64
    // so that off-viewport content is fully preserved on reload.
    const json = this.canvas.toJSON(['name', 'src', 'crossOrigin']);
    const canvasObjects = this.canvas.getObjects();

    // Convert any external-URL image sources to inline base64.
    // Objects in toJSON() are in the same order as getObjects().
    if (json.objects) {
      json.objects.forEach((obj, i) => {
        if (obj.type === 'image' && obj.src && !obj.src.startsWith('data:')) {
          try {
            const fabricObj = canvasObjects[i];
            if (fabricObj && fabricObj.type === 'image' && fabricObj._element) {
              const el = fabricObj._element;
              const c = document.createElement('canvas');
              c.width = el.naturalWidth || el.width;
              c.height = el.naturalHeight || el.height;
              const ctx = c.getContext('2d');
              ctx.drawImage(el, 0, 0);
              obj.src = c.toDataURL('image/png');
            }
          } catch (e) {
            console.warn('Could not embed image as base64:', e);
          }
        }
      });
    }

    return JSON.stringify(json);
  };

  handleExport = () => {
    try {
      const dataURL = this.exportAsImage('png');
      const designJson = this.exportDesignJson();
      if (this.props.onExport) {
        this.props.onExport(dataURL, designJson);
      }
    } catch (e) {
      // toDataURL throws a SecurityError if any layer tainted the canvas
      // (a remote image that couldn't be loaded CORS-clean).
      console.error('Export failed:', e);
      this._showError('Exportul a eșuat. O imagine din editor nu permite editarea (CORS).');
    }
  };

  handleDownload = () => {
    try {
      const dataURL = this.exportAsImage('png');
      const a = document.createElement('a');
      a.href = dataURL;
      a.download = 'design.png';
      a.click();
    } catch (e) {
      console.error('Download failed:', e);
      this._showError('Descărcarea a eșuat. O imagine din editor nu permite editarea (CORS).');
    }
  };

  // ===========================================================================
  // Align helpers
  // ===========================================================================
  alignObject = (alignment) => {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    const { canvasWidth, canvasHeight } = this.state;

    // Normalize to a top-left origin without moving the object, so the math is
    // correct regardless of the object's current origin (images load with a
    // center origin, shapes/text with top-left). Without this, mixing center
    // and edge aligns makes the object jump partly off-canvas.
    const topLeft = obj.getPointByOrigin('left', 'top');
    obj.set({ originX: 'left', originY: 'top', left: topLeft.x, top: topLeft.y });

    const w = obj.getScaledWidth();
    const h = obj.getScaledHeight();

    switch (alignment) {
      case 'left': obj.set({ left: 0 }); break;
      case 'center-h': obj.set({ left: (canvasWidth - w) / 2 }); break;
      case 'right': obj.set({ left: canvasWidth - w }); break;
      case 'top': obj.set({ top: 0 }); break;
      case 'center-v': obj.set({ top: (canvasHeight - h) / 2 }); break;
      case 'bottom': obj.set({ top: canvasHeight - h }); break;
      case 'center': obj.set({ left: (canvasWidth - w) / 2, top: (canvasHeight - h) / 2 }); break;
      default: break;
    }
    obj.setCoords();
    this.canvas.renderAll();
    this.saveHistory();
  };

  // ===========================================================================
  // Background removal (client-side AI)
  // ===========================================================================
  removeBackground = async () => {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return;

    this.setState({ removingBg: true });
    try {
      const { removeBackground } = await import('@imgly/background-removal');

      // Get the image source as a blob
      const src = obj.getSrc();
      let blob;
      if (src.startsWith('data:')) {
        const res = await fetch(src);
        blob = await res.blob();
      } else {
        const res = await fetch(src, { mode: 'cors' });
        blob = await res.blob();
      }

      const resultBlob = await removeBackground(blob, {
        output: { format: 'image/png' },
        model: 'isnet_fp16',
        progress: (key, current, total) => {
          if (total > 0) {
            const pct = Math.round((current / total) * 100);
            this.setState({ removingBgProgress: `${key.split('/').pop()}: ${pct}%` });
          }
        },
      });

      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(resultBlob);
      });

      // Replace the image on canvas, keeping position/scale
      const props = {
        left: obj.left,
        top: obj.top,
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
        angle: obj.angle,
        originX: obj.originX,
        originY: obj.originY,
        name: (obj.name || 'Image') + ' (no bg)',
      };

      fabric.Image.fromURL(dataUrl, (newImg) => {
        newImg.set(props);
        const idx = this.canvas.getObjects().indexOf(obj);
        this.canvas.remove(obj);
        // Insert at same z-index
        this.canvas.insertAt(newImg, idx, false);
        this.canvas.setActiveObject(newImg);
        this.canvas.renderAll();
        this.saveHistory();
        this.updateLayers();
        this.setState({ removingBg: false, removingBgProgress: null });
      }, { crossOrigin: 'anonymous' });
    } catch (err) {
      console.error('Background removal failed:', err);
      this.setState({ removingBg: false, removingBgProgress: null });
    }
  };

  // ===========================================================================
  // Magic Upscale
  // ===========================================================================
  magicUpscale = async (factor) => {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return;

    this.setState({ upscaling: true, showUpscaleMenu: false });
    try {
      const el = obj._element;
      const srcW = el.naturalWidth || el.width;
      const srcH = el.naturalHeight || el.height;
      const dstW = srcW * factor;
      const dstH = srcH * factor;

      // Step 1: Draw source at native size
      const srcCanvas = document.createElement('canvas');
      srcCanvas.width = srcW;
      srcCanvas.height = srcH;
      const srcCtx = srcCanvas.getContext('2d');
      srcCtx.drawImage(el, 0, 0, srcW, srcH);

      // Step 2: Multi-step upscale (doubling each step for quality)
      let current = srcCanvas;
      let curW = srcW, curH = srcH;
      while (curW < dstW || curH < dstH) {
        const nextW = Math.min(curW * 2, dstW);
        const nextH = Math.min(curH * 2, dstH);
        const step = document.createElement('canvas');
        step.width = nextW;
        step.height = nextH;
        const ctx = step.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(current, 0, 0, nextW, nextH);
        current = step;
        curW = nextW;
        curH = nextH;
      }

      // Step 3: Unsharp mask sharpening for crisp AI-like result
      await new Promise(r => setTimeout(r, 10));
      const dstCtx = current.getContext('2d');
      const imageData = dstCtx.getImageData(0, 0, dstW, dstH);
      this._unsharpMask(imageData, dstW, dstH, 1.2, 1);
      dstCtx.putImageData(imageData, 0, 0);

      const resultUrl = current.toDataURL('image/png');

      // Replace image on canvas, keeping position/scale
      const props = {
        left: obj.left,
        top: obj.top,
        scaleX: obj.scaleX / factor,
        scaleY: obj.scaleY / factor,
        angle: obj.angle,
        originX: obj.originX,
        originY: obj.originY,
        name: (obj.name || 'Image') + ` (${factor}x)`,
      };

      fabric.Image.fromURL(resultUrl, (newImg) => {
        newImg.set(props);
        const idx = this.canvas.getObjects().indexOf(obj);
        this.canvas.remove(obj);
        this.canvas.insertAt(newImg, idx, false);
        this.canvas.setActiveObject(newImg);
        this.canvas.renderAll();
        this.saveHistory();
        this.updateLayers();
        this.setState({ upscaling: false });
      }, { crossOrigin: 'anonymous' });
    } catch (err) {
      console.error('Upscale failed:', err);
      this.setState({ upscaling: false });
    }
  };

  /**
   * Unsharp mask: sharpen an ImageData in place.
   * amount: strength (0-3 typical), radius is fixed at 1px for speed.
   */
  _unsharpMask = (imageData, w, h, amount = 1.2, passes = 1) => {
    const px = imageData.data;
    for (let pass = 0; pass < passes; pass++) {
      const orig = new Uint8ClampedArray(px);
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = (y * w + x) * 4;
          for (let c = 0; c < 3; c++) {
            // 3x3 Gaussian blur approximation
            const blur = (
              orig[((y-1)*w+(x-1))*4+c] + orig[((y-1)*w+x)*4+c]*2 + orig[((y-1)*w+(x+1))*4+c] +
              orig[(y*w+(x-1))*4+c]*2   + orig[i+c]*4              + orig[(y*w+(x+1))*4+c]*2 +
              orig[((y+1)*w+(x-1))*4+c] + orig[((y+1)*w+x)*4+c]*2 + orig[((y+1)*w+(x+1))*4+c]
            ) / 16;
            px[i + c] = Math.max(0, Math.min(255, Math.round(orig[i + c] + (orig[i + c] - blur) * amount)));
          }
        }
      }
    }
  };

  // ===========================================================================
  // Magic Erase (paint-to-remove inpainting)
  // ===========================================================================
  enterMagicErase = () => {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return;

    this._magicEraseTarget = obj;
    this._magicEraseStrokes = [];

    // Lock all objects so only drawing works
    this.canvas.getObjects().forEach(o => {
      o._prevSelectable = o.selectable;
      o.selectable = false;
      o.evented = false;
    });

    this.canvas.isDrawingMode = true;
    this.canvas.freeDrawingBrush = new fabric.PencilBrush(this.canvas);
    this.canvas.freeDrawingBrush.color = 'rgba(255, 60, 60, 0.45)';
    this.canvas.freeDrawingBrush.width = this.state.magicEraseBrushSize;

    this._magicErasePathHandler = (e) => {
      if (e.path) {
        e.path._isMagicErase = true;
        e.path.selectable = false;
        e.path.evented = false;
        this._magicEraseStrokes.push(e.path);
      }
    };
    this.canvas.on('path:created', this._magicErasePathHandler);

    this.setState({ magicEraseMode: true });
  };

  cancelMagicErase = () => {
    // Remove all drawn strokes
    if (this._magicEraseStrokes) {
      this._magicEraseStrokes.forEach(s => this.canvas.remove(s));
    }
    this._exitMagicEraseMode();
  };

  _exitMagicEraseMode = () => {
    this.canvas.isDrawingMode = false;
    this.canvas.off('path:created', this._magicErasePathHandler);

    // Restore selectability
    this.canvas.getObjects().forEach(o => {
      if (o._prevSelectable !== undefined) {
        o.selectable = o._prevSelectable;
        o.evented = true;
        delete o._prevSelectable;
      }
    });

    this._magicEraseTarget = null;
    this._magicEraseStrokes = [];
    this.canvas.renderAll();
    this.setState({ magicEraseMode: false, magicEraseApplying: false });
  };

  handleMagicEraseBrushSize = (e) => {
    const size = parseInt(e.target.value, 10);
    this.setState({ magicEraseBrushSize: size });
    if (this.canvas.freeDrawingBrush) {
      this.canvas.freeDrawingBrush.width = size;
    }
  };

  applyMagicErase = async () => {
    const img = this._magicEraseTarget;
    const strokes = this._magicEraseStrokes;
    if (!img || !strokes.length) return;

    this.setState({ magicEraseApplying: true });

    try {
      const imgEl = img._element;
      const natW = imgEl.naturalWidth || imgEl.width;
      const natH = imgEl.naturalHeight || imgEl.height;

      // Draw original image at natural resolution
      const imgCanvas = document.createElement('canvas');
      imgCanvas.width = natW;
      imgCanvas.height = natH;
      const imgCtx = imgCanvas.getContext('2d');
      imgCtx.drawImage(imgEl, 0, 0, natW, natH);

      // Build mask at natural resolution
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = natW;
      maskCanvas.height = natH;
      const maskCtx = maskCanvas.getContext('2d');
      maskCtx.fillStyle = 'black';
      maskCtx.fillRect(0, 0, natW, natH);

      // Transform strokes from canvas space to image natural space
      const bound = img.getBoundingRect(true);
      const sx = natW / bound.width;
      const sy = natH / bound.height;

      maskCtx.strokeStyle = 'white';
      maskCtx.lineJoin = 'round';
      maskCtx.lineCap = 'round';

      strokes.forEach(pathObj => {
        const cmds = pathObj.path;
        if (!cmds) return;
        maskCtx.lineWidth = (pathObj.strokeWidth || 30) * sx;
        maskCtx.beginPath();
        cmds.forEach(cmd => {
          // Path commands from PencilBrush are in absolute canvas coordinates
          switch (cmd[0]) {
            case 'M':
              maskCtx.moveTo((cmd[1] - bound.left) * sx, (cmd[2] - bound.top) * sy);
              break;
            case 'Q':
              maskCtx.quadraticCurveTo(
                (cmd[1] - bound.left) * sx, (cmd[2] - bound.top) * sy,
                (cmd[3] - bound.left) * sx, (cmd[4] - bound.top) * sy
              );
              break;
            case 'L':
              maskCtx.lineTo((cmd[1] - bound.left) * sx, (cmd[2] - bound.top) * sy);
              break;
            default: break;
          }
        });
        maskCtx.stroke();
      });

      // Get pixel data
      const imageData = imgCtx.getImageData(0, 0, natW, natH);
      const maskData = maskCtx.getImageData(0, 0, natW, natH);

      // Run inpainting (async to avoid blocking UI)
      await new Promise(resolve => setTimeout(resolve, 10));
      this._inpaint(imageData, maskData);

      // Put result back
      imgCtx.putImageData(imageData, 0, 0);
      const resultUrl = imgCanvas.toDataURL('image/png');

      // Replace image on canvas
      fabric.Image.fromURL(resultUrl, (newImg) => {
        newImg.set({
          left: img.left,
          top: img.top,
          scaleX: img.scaleX,
          scaleY: img.scaleY,
          angle: img.angle,
          originX: img.originX,
          originY: img.originY,
          name: (img.name || 'Image') + ' (erased)',
        });

        const idx = this.canvas.getObjects().indexOf(img);
        strokes.forEach(s => this.canvas.remove(s));
        this.canvas.remove(img);
        this.canvas.insertAt(newImg, idx, false);
        this.canvas.setActiveObject(newImg);
        this.canvas.renderAll();
        this.saveHistory();
        this.updateLayers();
        this._exitMagicEraseMode();
      }, { crossOrigin: 'anonymous' });
    } catch (err) {
      console.error('Magic erase failed:', err);
      this._exitMagicEraseMode();
    }
  };

  /**
   * BFS boundary-propagation inpainting.
   * Fills masked pixels layer by layer from the outside in, averaging
   * non-masked neighbours. O(masked_pixels) — fast even on large images.
   */
  _inpaint = (imageData, maskData) => {
    const w = imageData.width, h = imageData.height;
    const px = imageData.data;

    // Build binary mask: 1 = needs fill
    const isMasked = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) {
      isMasked[i] = maskData.data[i * 4] > 128 ? 1 : 0;
    }

    // Dilate mask by 1px to cover anti-aliased edges
    const dilated = new Uint8Array(isMasked);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        if (isMasked[y * w + x]) {
          for (let dy = -1; dy <= 1; dy++)
            for (let dx = -1; dx <= 1; dx++)
              dilated[(y + dy) * w + (x + dx)] = 1;
        }
      }
    }
    for (let i = 0; i < w * h; i++) isMasked[i] = dilated[i];

    const DX = [-1, 0, 1, -1, 1, -1, 0, 1];
    const DY = [-1, -1, -1, 0, 0, 1, 1, 1];

    // Seed queue with masked pixels that touch a non-masked pixel
    let queue = [];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = y * w + x;
        if (!isMasked[idx]) continue;
        for (let d = 0; d < 8; d++) {
          const nx = x + DX[d], ny = y + DY[d];
          if (nx >= 0 && nx < w && ny >= 0 && ny < h && !isMasked[ny * w + nx]) {
            queue.push(idx);
            break;
          }
        }
      }
    }

    // Detect whether the masked region borders mostly-transparent pixels.
    // If the average alpha of border neighbours is low, erase to transparent
    // instead of inpainting.
    let borderAlphaSum = 0, borderAlphaCount = 0;
    for (let q = 0; q < queue.length; q++) {
      const idx = queue[q];
      const x = idx % w, y = (idx / w) | 0;
      for (let d = 0; d < 8; d++) {
        const nx = x + DX[d], ny = y + DY[d];
        if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
          const ni = ny * w + nx;
          if (!isMasked[ni]) {
            borderAlphaSum += px[ni * 4 + 3];
            borderAlphaCount++;
          }
        }
      }
    }
    const avgBorderAlpha = borderAlphaCount > 0 ? borderAlphaSum / borderAlphaCount : 255;
    const eraseToTransparent = avgBorderAlpha < 128;

    if (eraseToTransparent) {
      // Simply make all masked pixels fully transparent
      for (let i = 0; i < w * h; i++) {
        if (isMasked[i]) {
          const pi = i * 4;
          px[pi] = 0;
          px[pi + 1] = 0;
          px[pi + 2] = 0;
          px[pi + 3] = 0;
        }
      }
    } else {
      // BFS: fill one layer at a time (original inpainting for opaque images)
      while (queue.length > 0) {
        const nextQueue = [];
        for (let q = 0; q < queue.length; q++) {
          const idx = queue[q];
          if (!isMasked[idx]) continue;
          const x = idx % w, y = (idx / w) | 0;
          let r = 0, g = 0, b = 0, a = 0, count = 0;

          for (let d = 0; d < 8; d++) {
            const nx = x + DX[d], ny = y + DY[d];
            if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
              const ni = ny * w + nx;
              if (!isMasked[ni]) {
                const pi = ni * 4;
                r += px[pi]; g += px[pi + 1]; b += px[pi + 2]; a += px[pi + 3];
                count++;
              }
            }
          }

          if (count > 0) {
            const pi = idx * 4;
            px[pi]     = (r / count) | 0;
            px[pi + 1] = (g / count) | 0;
            px[pi + 2] = (b / count) | 0;
            px[pi + 3] = (a / count) | 0;
            isMasked[idx] = 0;
            // Enqueue masked neighbours for next layer
            for (let d = 0; d < 8; d++) {
              const nx = x + DX[d], ny = y + DY[d];
              if (nx >= 0 && nx < w && ny >= 0 && ny < h && isMasked[ny * w + nx]) {
                nextQueue.push(ny * w + nx);
              }
            }
          }
        }
        queue = nextQueue;
      }
    }

    // Smooth pass: blur only the originally-masked region for cleaner edges
    const wasInpainted = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) wasInpainted[i] = dilated[i];

    const tmpPx = new Uint8ClampedArray(px);
    for (let pass = 0; pass < 3; pass++) {
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          if (!wasInpainted[y * w + x]) continue;
          let r = 0, g = 0, b = 0, a = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const pi = ((y + dy) * w + (x + dx)) * 4;
              r += tmpPx[pi]; g += tmpPx[pi + 1]; b += tmpPx[pi + 2]; a += tmpPx[pi + 3];
            }
          }
          const pi = (y * w + x) * 4;
          px[pi]     = (r / 9) | 0;
          px[pi + 1] = (g / 9) | 0;
          px[pi + 2] = (b / 9) | 0;
          px[pi + 3] = (a / 9) | 0;
        }
      }
      tmpPx.set(px);
    }
  };

  // ===========================================================================
  // Render
  // ===========================================================================
  render() {
    const {
      activeTool, activeObject, fillColor, strokeColor, opacity,
      fontSize, fontFamily, fontBold, fontItalic, fontSearch, fontDropdownOpen,
      canvasWidth, canvasHeight, zoom, layers, history, historyIndex, showEventImages,
      shadowEnabled, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY,
      errorMessage,
    } = this.state;
    const eventImages = this.props.eventImages || [];
    const isText = activeObject && activeObject.type === 'i-text';
    const isImage = activeObject && activeObject.type === 'image';
    const { removingBg, removingBgProgress, magicEraseMode, magicEraseApplying, magicEraseBrushSize } = this.state;
    const filteredFonts = isText ? this.getFilteredFonts() : [];
    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < history.length - 1;

    return (
      <div className="de-editor" ref={this.editorRef}>
        {errorMessage && (
          <div className="de-error-banner" onClick={() => this.setState({ errorMessage: null })}>
            ⚠ {errorMessage}
          </div>
        )}
        {/* Top toolbar */}
        <div className="de-toolbar">
          <div className="de-toolbar-group">
            <button className={`de-btn ${activeTool === 'select' ? 'active' : ''}`} onClick={() => this.setTool('select')} title="Select (V)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M7 2l10 10-4 1 3 7-2 1-3-7-4 4z" fill="currentColor"/></svg>
            </button>
          </div>

          <div className="de-toolbar-sep" />

          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.addText} title="Add Text (T)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 4v3h5.5v12h3V7H19V4z" fill="currentColor"/></svg>
            </button>
            <button className="de-btn" onClick={this.addRect} title="Rectangle">
              <svg viewBox="0 0 24 24" width="18" height="18"><rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/></svg>
            </button>
            <button className="de-btn" onClick={this.addCircle} title="Circle">
              <svg viewBox="0 0 24 24" width="18" height="18"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/></svg>
            </button>
            <button className="de-btn" onClick={this.addLine} title="Line">
              <svg viewBox="0 0 24 24" width="18" height="18"><line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" strokeWidth="2"/></svg>
            </button>
            <button className={`de-btn ${activeTool === 'draw' ? 'active' : ''}`} onClick={() => this.setTool('draw')} title="Free Draw">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75z" fill="currentColor"/></svg>
            </button>
          </div>

          <div className="de-toolbar-sep" />

          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.triggerImageUpload} title="Add Image">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M21 19V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5l3.5-4.5z" fill="currentColor"/></svg>
            </button>
            <input ref={this.fileInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={this.handleImageUpload} />
          </div>

          {/* Insert event image */}
          {eventImages.length > 0 && (
            <>
              <div className="de-toolbar-group de-dropdown-wrap">
                <button className="de-btn" onClick={() => this.setState(p => ({ showEventImages: !p.showEventImages }))} title="Insert Event Image">
                  📎 Event
                </button>
                {showEventImages && (
                  <div className="de-dropdown">
                    {eventImages.map((img, i) => (
                      img.src ? (
                        <button key={i} className="de-dropdown-item" onClick={() => this.insertEventImage(img.src)}>
                          {img.name}
                        </button>
                      ) : null
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="de-toolbar-sep" />

          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.undo} disabled={!canUndo} title="Undo (Ctrl+Z)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M12.5 8c-2.65 0-5.05 1.04-6.83 2.72L3 8v9h9l-2.83-2.83C10.51 12.83 11.91 12.25 13.5 12.25c2.65 0 5.05 1.04 6.83 2.72l2.95-2.95C21.12 9.47 17.05 8 12.5 8z" fill="currentColor"/></svg>
            </button>
            <button className="de-btn" onClick={this.redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16a8.003 8.003 0 017.6-5.5c1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" fill="currentColor"/></svg>
            </button>
          </div>

          <div className="de-toolbar-sep" />

          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.deleteSelected} disabled={!activeObject} title="Delete">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M6 19a2 2 0 002 2h8a2 2 0 002-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/></svg>
            </button>
            <button className="de-btn" onClick={this.duplicateObject} disabled={!activeObject} title="Duplicate (Ctrl+D)">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M16 1H4a2 2 0 00-2 2v14h2V3h12V1zm3 4H8a2 2 0 00-2 2v14a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2zm0 16H8V7h11v14z" fill="currentColor"/></svg>
            </button>
            {isImage && !magicEraseMode && (
              <button className="de-btn" onClick={this.removeBackground} disabled={removingBg} title="Remove Background (AI)">
                {removingBg ? (removingBgProgress ? `⌛️ ${removingBgProgress}` : '⌛️ Loading model…') : '✨ Remove BG'}
              </button>
            )}
            {isImage && !magicEraseMode && (
              <button className="de-btn" onClick={this.enterMagicErase} title="Magic Erase – paint over area to remove">
                🪄 Magic Erase
              </button>
            )}
            {isImage && !magicEraseMode && (
              <div className="de-dropdown-wrap" style={{ position: 'relative', display: 'inline-block' }}>
                <button className="de-btn" disabled={this.state.upscaling} onClick={() => this.setState(p => ({ showUpscaleMenu: !p.showUpscaleMenu }))} title="Magic Upscale">
                  {this.state.upscaling ? '⌛ Upscaling…' : '🔎 Upscale'}
                </button>
                {this.state.showUpscaleMenu && (
                  <div className="de-dropdown">
                    {[2, 4, 6, 8].map(f => (
                      <button key={f} className="de-dropdown-item" onClick={() => this.magicUpscale(f)}>
                        {f}× Upscale
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Magic Erase toolbar */}
          {magicEraseMode && (
            <div className="de-toolbar-group de-magic-erase-bar">
              <span className="de-magic-erase-label">🪄 Paint over area to erase</span>
              <label className="de-label">Size</label>
              <input type="range" min="5" max="100" step="1" value={magicEraseBrushSize}
                onChange={this.handleMagicEraseBrushSize} className="de-range" style={{ width: 80 }} />
              <span className="de-val">{magicEraseBrushSize}</span>
              <button className="de-btn de-btn-primary" onClick={this.applyMagicErase} disabled={magicEraseApplying}>
                {magicEraseApplying ? '⌛ Applying...' : '✓ Apply'}
              </button>
              <button className="de-btn" onClick={this.cancelMagicErase} disabled={magicEraseApplying}>
                ✕ Cancel
              </button>
            </div>
          )}

          <div className="de-toolbar-sep" />

          {/* Ordering */}
          <div className="de-toolbar-group">
            <button className="de-btn" onClick={this.bringForward} disabled={!activeObject} title="Bring Forward">↑</button>
            <button className="de-btn" onClick={this.sendBackward} disabled={!activeObject} title="Send Backward">↓</button>
            <button className="de-btn" onClick={this.bringToFront} disabled={!activeObject} title="Bring to Front">⤒</button>
            <button className="de-btn" onClick={this.sendToBack} disabled={!activeObject} title="Send to Back">⤓</button>
          </div>

          <div className="de-toolbar-sep" />

          {/* Alignment */}
          <div className="de-toolbar-group">
            <button className="de-btn" onClick={() => this.alignObject('left')} disabled={!activeObject} title="Align Left">⫷</button>
            <button className="de-btn" onClick={() => this.alignObject('center-h')} disabled={!activeObject} title="Center H">⫿</button>
            <button className="de-btn" onClick={() => this.alignObject('right')} disabled={!activeObject} title="Align Right">⫸</button>
            <button className="de-btn" onClick={() => this.alignObject('top')} disabled={!activeObject} title="Align Top">⫠</button>
            <button className="de-btn" onClick={() => this.alignObject('center-v')} disabled={!activeObject} title="Center V">⫥</button>
            <button className="de-btn" onClick={() => this.alignObject('bottom')} disabled={!activeObject} title="Align Bottom">⫡</button>
            <button className="de-btn" onClick={() => this.alignObject('center')} disabled={!activeObject} title="Center on canvas">◎</button>
          </div>

          <div className="de-toolbar-spacer" />

          {/* Export */}
          <div className="de-toolbar-group">
            <button className="de-btn de-btn-primary" onClick={this.handleExport} title="Export & Use">
              ✓ Folosește
            </button>
            <button className="de-btn" onClick={this.handleDownload} title="Download PNG">
              ⬇ Download
            </button>
          </div>
        </div>

        {/* Main area */}
        <div className="de-main">
          {/* Left: Properties panel */}
          <div className="de-panel de-panel-left">
            {/* Canvas settings */}
            <div className="de-panel-section">
              <div className="de-panel-title">Canvas</div>
              <select className="de-select" onChange={this.handlePresetChange}>
                {PRESETS.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
              </select>
              <div className="de-row">
                <label className="de-label">W</label>
                <input type="number" className="de-input-sm" value={canvasWidth}
                  onChange={(e) => this.resizeCanvas(parseInt(e.target.value, 10) || 100, canvasHeight)} />
                <label className="de-label">H</label>
                <input type="number" className="de-input-sm" value={canvasHeight}
                  onChange={(e) => this.resizeCanvas(canvasWidth, parseInt(e.target.value, 10) || 100)} />
              </div>
              <div className="de-row">
                <label className="de-label">BG</label>
                <input type="color" className="de-color" value={this.state.bgColor || '#ffffff'}
                  onChange={this.handleBgColorChange} />
                <button className={`de-btn-sm ${!this.state.bgColor ? 'active' : ''}`} onClick={this.clearBgColor} title="Transparent background">
                  ⊘
                </button>
              </div>
            </div>

            {/* Colors */}
            <div className="de-panel-section">
              <div className="de-panel-title">Colors</div>
              <div className="de-row">
                <label className="de-label">Fill</label>
                <input type="color" className="de-color" value={fillColor} onChange={this.handleFillChange} />
              </div>
              <div className="de-row">
                <label className="de-label">Stroke</label>
                <input type="color" className="de-color" value={strokeColor} onChange={this.handleStrokeChange} />
              </div>
              <div className="de-row">
                <label className="de-label">Opacity</label>
                <input type="range" min="0" max="1" step="0.05" value={opacity} onChange={this.handleOpacityChange}
                  className="de-range" />
              </div>
            </div>

            {/* Shadow / Glow */}
            {activeObject && (
              <div className="de-panel-section">
                <div className="de-panel-title">
                  Shadow / Glow
                  <button
                    className={`de-btn-sm ${shadowEnabled ? 'active' : ''}`}
                    onClick={this.toggleShadow}
                    style={{ marginLeft: 'auto', fontSize: 11 }}
                  >
                    {shadowEnabled ? 'ON' : 'OFF'}
                  </button>
                </div>
                {shadowEnabled && (
                  <>
                    <div className="de-row">
                      <label className="de-label">Color</label>
                      <input type="color" className="de-color" value={shadowColor} onChange={this.handleShadowColor} />
                    </div>
                    <div className="de-row">
                      <label className="de-label">Blur</label>
                      <input type="range" min="0" max="60" step="1" value={shadowBlur} onChange={this.handleShadowBlur} className="de-range" />
                      <span className="de-val">{shadowBlur}</span>
                    </div>
                    <div className="de-row">
                      <label className="de-label">X</label>
                      <input type="range" min="-50" max="50" step="1" value={shadowOffsetX} onChange={this.handleShadowOffsetX} className="de-range" />
                      <span className="de-val">{shadowOffsetX}</span>
                    </div>
                    <div className="de-row">
                      <label className="de-label">Y</label>
                      <input type="range" min="-50" max="50" step="1" value={shadowOffsetY} onChange={this.handleShadowOffsetY} className="de-range" />
                      <span className="de-val">{shadowOffsetY}</span>
                    </div>
                    <div className="de-row">
                      <button className="de-btn-sm" onClick={this.applyGlowPreset} title="Apply neon glow preset">
                        ✨ Glow
                      </button>
                      <button className="de-btn-sm" onClick={() => this.setState({
                        shadowColor: '#000000', shadowBlur: 10, shadowOffsetX: 4, shadowOffsetY: 4
                      }, () => this._applyShadow())} title="Apply drop shadow preset">
                        🖤 Drop
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Text properties */}
            {isText && (
              <div className="de-panel-section">
                <div className="de-panel-title">Text</div>
                <div className="de-font-picker" ref={this.fontPickerRef}>
                  <input
                    type="text"
                    className="de-select"
                    placeholder="Search fonts..."
                    value={fontDropdownOpen ? fontSearch : fontFamily}
                    onFocus={() => {
                      this.setState({ fontDropdownOpen: true, fontSearch: '' });
                      // Preload visible fonts so they render in their own typeface
                      const fonts = this.getFilteredFonts();
                      fonts.forEach(f => loadGoogleFont(f));
                    }}
                    onChange={(e) => {
                      this.setState({ fontSearch: e.target.value }, () => {
                        const fonts = this.getFilteredFonts();
                        fonts.forEach(f => loadGoogleFont(f));
                      });
                    }}
                    onBlur={() => setTimeout(() => this.setState({ fontDropdownOpen: false }), 200)}
                  />
                  {fontDropdownOpen && (
                    <div className="de-font-dropdown">
                      {filteredFonts.map(f => (
                        <button
                          key={f}
                          className={`de-font-option ${f === fontFamily ? 'active' : ''}`}
                          style={{ fontFamily: `"${f}", sans-serif` }}
                          onMouseDown={(e) => { e.preventDefault(); this.handleFontFamilyChange(f); }}
                        >
                          {f}
                        </button>
                      ))}
                      {filteredFonts.length === 0 && (
                        <div className="de-font-empty">No fonts found</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="de-row">
                  <label className="de-label">Size</label>
                  <input type="number" className="de-input-sm" value={fontSize} onChange={this.handleFontSizeChange} min="8" max="200" />
                  <button className={`de-btn-sm ${fontBold ? 'active' : ''}`} onClick={this.toggleBold}><b>B</b></button>
                  <button className={`de-btn-sm ${fontItalic ? 'active' : ''}`} onClick={this.toggleItalic}><i>I</i></button>
                </div>
              </div>
            )}
          </div>

          {/* Center: Canvas */}
          <div className="de-canvas-area" ref={this.canvasAreaRef} onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}>
            <div className="de-canvas-centering">
              <div className="de-canvas-sizer" ref={this.canvasSizerRef} style={{
                width: (canvasWidth + 2 * BLEED) * zoom,
                height: (canvasHeight + 2 * BLEED) * zoom,
                flexShrink: 0,
              }}>
                <div className="de-canvas-wrapper" style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top left',
                  width: canvasWidth + 2 * BLEED,
                  height: canvasHeight + 2 * BLEED,
                }}>
                  {/* Artboard paper (checkerboard + shadow), inset by the bleed — behind the canvas. */}
                  <div className="de-artboard" style={{
                    left: BLEED,
                    top: BLEED,
                    width: canvasWidth,
                    height: canvasHeight,
                  }} />
                  <canvas ref={this.canvasRef} />
                  {/* Artboard outline — paints above any content so the boundary stays visible. */}
                  <div className="de-artboard-outline" style={{
                    left: BLEED,
                    top: BLEED,
                    width: canvasWidth,
                    height: canvasHeight,
                  }} />
                </div>
              </div>
            </div>
            <div className="de-canvas-info">
              {canvasWidth} × {canvasHeight}px — {Math.round(zoom * 100)}%
              {isImage && activeObject._element && (
                <span style={{ marginLeft: 12 }}>
                  | Image: {activeObject._element.naturalWidth || activeObject._element.width} × {activeObject._element.naturalHeight || activeObject._element.height}px
                </span>
              )}
            </div>

            {/* Zoom / view controls */}
            <div className="de-zoom-bar">
              <button className="de-btn" onClick={this.zoomOut} title="Micșorează (Ctrl -)">−</button>
              <span className="de-zoom-level">{Math.round(zoom * 100)}%</span>
              <button className="de-btn" onClick={this.zoomIn} title="Mărește (Ctrl +)">+</button>
              <div className="de-toolbar-sep" />
              <button className="de-btn" onClick={this.zoomTo100} title="Zoom 100% (Ctrl 0)">100%</button>
              <button className="de-btn" onClick={this.fitCanvasInView} title="Potrivește în ecran">⤢ Fit</button>
              <button className="de-btn" onClick={this.scrollCanvasToCenter} title="Centrează vizualizarea">⊙</button>
            </div>
          </div>

          {/* Right: Layers panel */}
          <div className="de-panel de-panel-right">
            <div className="de-panel-section">
              <div className="de-panel-title">Layers</div>
              <div className="de-layers">
                {layers.map((layer) => (
                  <div
                    key={layer.index}
                    className={`de-layer ${activeObject === this.canvas?.item(layer.index) ? 'active' : ''}`}
                    onClick={() => this.selectLayer(layer.index)}
                  >
                    <button
                      className="de-layer-vis"
                      onClick={(e) => { e.stopPropagation(); this.toggleLayerVisibility(layer.index); }}
                    >
                      {layer.visible ? '👁' : '🚫'}
                    </button>
                    <span className="de-layer-icon">
                      {layer.type === 'i-text' ? 'T' : layer.type === 'image' ? '🖼' : '◻'}
                    </span>
                    <span className="de-layer-name">{layer.name}</span>
                  </div>
                ))}
                {layers.length === 0 && (
                  <div className="de-layers-empty">No layers</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DesignEditor;
